import React, { FC, useState } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import useValueChangeEffect from '../../hooks/useValueChangeEffect';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Card, Dropdown, Empty, Menu } from 'antd';
import { FileTextOutlined, LinkOutlined, MoreOutlined } from '@ant-design/icons';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import { COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS } from '../../actions/compliance_request_instances_actions';
import {
	complianceRequestInstancesRestCrudThunks,
	fetchComplianceSummary,
} from '../../thunks/compliance_request_instances_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { COMPLIANCE_EXPIRY_STATUSES } from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { omitStatusFilters } from '../../utils/ComplianceUtils';
import {
	COMPLIANCE_EXPIRY_STATUS_COLORS,
	COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES,
	SUPPLIER_COMPLIANCE_PIPELINE_FILTER_ITEMS,
} from '../compliance_common/ComplianceConstants';
import { PipelineFilter } from '../QuotesTable/pipelineFilters/pipelineFilters';
import { ComplianceStatusDisplay } from '../compliance_common/ComplianceStatusDisplay';
import { ComplianceEvaluationSummary } from '../compliance_common/ComplianceEvaluationSummary';
import { ComplianceEvaluationModal } from '../compliance_common/ComplianceEvaluationModal';
import ChooseComplianceDocumentModal from './ChooseComplianceDocumentModal';

const REQUESTS_LIST_TARGET_COLLECTION = 'supplierComplianceRequestsList';
const INSTANCES_COLLECTION = 'compliance_request_instances';
const INITIAL_PAGINATION = { current: 1, pageSize: 10 };
const STATUS_COUNTS_NAME = 'supplierRequestsStatusCounts';

// PaginatedReduxTable dispatches its fetchData prop itself, so it must be the raw
// thunk-returning read method (not a pre-dispatched wrapper).
const fetchInstances = complianceRequestInstancesRestCrudThunks(ROLE_TYPES.SUPPLIER).read;

const formatDate = (isoDate) => (isoDate ? moment(isoDate).format('D MMM, YYYY') : '--');

interface SupplierComplianceRequestsPageProps {
	statusCounts: { [status: string]: number };
	filters: any;
	pagination: any;
	refetchInstances: (filters: any, pagination: any) => void;
	fetchStatusCounts: (countFilters: any) => Promise<any>;
	// Refetches the shell's Non-Compliant client banner after a link flips compliance.
	fetchSummary: () => Promise<any>;
	updateFilters: (filters: any, targetCollectionName: string) => void;
}

const SupplierComplianceRequestsPage: FC<SupplierComplianceRequestsPageProps> = ({
	statusCounts,
	filters,
	pagination,
	refetchInstances,
	fetchStatusCounts,
	fetchSummary,
	updateFilters,
}): React.ReactElement => {
	const [linkingInstance, setLinkingInstance] = useState(null);
	const [viewingEvaluationInstance, setViewingEvaluationInstance] = useState(null);

	// Chip counts follow every non-status filter (the grid is filtered server-side by
	// the same values via PaginatedReduxTable).
	const countFiltersKey = JSON.stringify(omitStatusFilters(filters));

	useMountEffect(() => {
		fetchStatusCounts(omitStatusFilters(filters));
	});
	useValueChangeEffect(countFiltersKey, () => {
		fetchStatusCounts(omitStatusFilters(filters));
	});

	const statuses = nullSafeGet('statuses', filters);
	const pipelineValue = { statuses };
	const handlePipelineChange = (nextValue) =>
		updateFilters(
			{ statuses: nullSafeGet('statuses', nextValue) },
			REQUESTS_LIST_TARGET_COLLECTION
		);

	const openLinkModal = (instance) => setLinkingInstance(instance);
	const closeLinkModal = () => setLinkingInstance(null);
	const closeEvaluationModal = () => setViewingEvaluationInstance(null);
	const handleLinked = () => {
		setLinkingInstance(null);
		refetchInstances(filters, pagination);
		fetchStatusCounts(omitStatusFilters(filters));
		// Linking a document can flip client compliance, so refresh the shell banner.
		fetchSummary();
	};

	// Left rail: "Past Due: <date>" (red) when overdue, else "Due: <date>".
	const renderDueRail = (instance) => {
		const dueBy = nullSafeGet('complianceRequest.dueBy', instance);
		if (!dueBy) {
			return <div className="w-24 shrink-0" />;
		}
		const isPastDue = moment(dueBy).isBefore(moment(), 'day');
		return (
			<div className="flex w-24 shrink-0 flex-col">
				<span className="footnoteregular text-character-secondary">
					{isPastDue ? 'Past Due:' : 'Due:'}
				</span>
				<span className="bodymedium" style={isPastDue ? { color: 'rgb(204, 50, 50)' } : undefined}>
					{moment(dueBy).format('D MMM')}
				</span>
			</div>
		);
	};

	// Status line: "<statusIcon> <Status>: <Compliance Type> (<country>)".
	const renderStatusLine = (instance) => {
		const request = nullSafeGetOrElse('complianceRequest', instance, {});
		const typeName = nullSafeGetOrElse('complianceType.name', request, '--');
		const countryCode = nullSafeGet('countryCode', request);
		return (
			<div className="bodymedium flex flex-row items-center">
				<ComplianceStatusDisplay status={instance.status} perspective="supplier" />
				<span>
					: {typeName}
					{countryCode ? ` (${countryCode})` : ''}
				</span>
			</div>
		);
	};

	// Meta row: Sample link • request name • Mandatory/Optional • Unique Document.
	const renderMeta = (instance) => {
		const request = nullSafeGetOrElse('complianceRequest', instance, {});
		const parts = [];
		parts.push(nullSafeGetOrElse('name', request, '--'));
		parts.push(request.isMandatory ? 'Mandatory' : 'Optional');
		if (request.requiresUniqueDocument) {
			parts.push('Unique Document');
		}
		return (
			<div className="footnoteregular flex flex-row flex-wrap items-center gap-1 text-character-secondary">
				<FileTextOutlined />
				{request.sampleFileUrl ? (
					<a href={request.sampleFileUrl} target="_blank" rel="noopener noreferrer">
						Sample
					</a>
				) : null}
				{request.sampleFileUrl ? <span>•</span> : null}
				<span>{parts.join(' • ')}</span>
			</div>
		);
	};

	// Right: the linked document + its expiration, or a "Link Document" CTA.
	const renderDoc = (instance) => {
		const linkedDocument = nullSafeGet('linkedComplianceDocument', instance);
		if (!linkedDocument) {
			const handleLinkClick = () => openLinkModal(instance);
			return (
				<Button icon={<LinkOutlined />} onClick={handleLinkClick}>
					Link Document
				</Button>
			);
		}
		const expiryStatus = nullSafeGet('expiryStatus', instance);
		const expiryDate = nullSafeGet('linkedComplianceDocument.expiryDate', instance);
		const expiryColor = COMPLIANCE_EXPIRY_STATUS_COLORS[expiryStatus];
		const expiryLabel =
			expiryStatus === COMPLIANCE_EXPIRY_STATUSES.EXPIRED
				? `Expired ${formatDate(expiryDate)}`
				: expiryStatus
				? `${COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES[expiryStatus]}: ${formatDate(expiryDate)}`
				: expiryDate
				? `Expiration Date: ${formatDate(expiryDate)}`
				: null;
		// The AI check stored when this document was linked. Absent for documents
		// linked before the check existed, or when the check was skipped — the
		// backend clears it on unlink, so its presence tracks the linked document.
		const checklistEvaluation = nullSafeGet('checklistEvaluation', instance);
		const openEvaluation = () => setViewingEvaluationInstance(instance);
		return (
			<div className="flex flex-col items-end gap-1">
				<a
					className="inline-flex flex-row items-center gap-1"
					href={linkedDocument.fileUrl}
					target="_blank"
					rel="noopener noreferrer"
				>
					<FileTextOutlined />
					{nullSafeGetOrElse(
						'fileName',
						linkedDocument,
						nullSafeGetOrElse('name', linkedDocument, 'document')
					)}
				</a>
				{checklistEvaluation ? (
					<ComplianceEvaluationSummary
						evaluation={checklistEvaluation}
						checklistItems={nullSafeGetOrElse(
							'complianceRequest.checklistExtraction.items',
							instance,
							[]
						)}
						onViewDetails={openEvaluation}
					/>
				) : null}
				{expiryLabel ? (
					<span
						className="footnoteregular"
						style={expiryColor ? { color: expiryColor } : { color: 'rgba(0, 0, 0, 0.45)' }}
					>
						{expiryLabel}
					</span>
				) : null}
			</div>
		);
	};

	// Kebab: View sample (Send note is deferred until a backend note endpoint exists).
	const renderActions = (instance) => {
		const sampleFileUrl = nullSafeGet('complianceRequest.sampleFileUrl', instance);
		const openSample = () => window.open(sampleFileUrl, '_blank', 'noreferrer');
		const menu = (
			<Menu>
				<Menu.Item key="sample" disabled={!sampleFileUrl} onClick={openSample}>
					View sample
				</Menu.Item>
			</Menu>
		);
		return (
			<Dropdown overlay={menu} trigger={['click']}>
				<Button icon={<MoreOutlined />} />
			</Dropdown>
		);
	};

	const renderRow = (instance) => {
		const request = nullSafeGetOrElse('complianceRequest', instance, {});
		const clientName =
			nullSafeGetOrElse('buyerCompanyName', instance, '') ||
			nullSafeGetOrElse('buyerCompany.name', instance, '--');
		return (
			<div className="flex flex-row items-start gap-4">
				{renderDueRail(instance)}
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<div className="flex flex-row flex-wrap items-baseline gap-2">
						<span className="bodymedium">{clientName}</span>
						{instance.createdAt ? (
							<span className="footnoteregular text-character-secondary">
								{formatDate(instance.createdAt)}
							</span>
						) : null}
					</div>
					{renderStatusLine(instance)}
					{renderMeta(instance)}
					{request.notes ? (
						<div className="footnoteregular border-0 border-l-2 border-solid border-grey-4 bg-grey-2 py-1 pl-2 text-character-secondary">
							{request.notes}
						</div>
					) : null}
				</div>
				<div className="flex shrink-0 flex-col items-end">{renderDoc(instance)}</div>
				<div className="shrink-0">{renderActions(instance)}</div>
			</div>
		);
	};

	// Single full-width column — the row layout is a card, not a set of columns.
	const tableColumns = [
		{
			title: '',
			key: 'row',
			render: (text, instance) => renderRow(instance),
		},
	];

	const pipelineCounts = SUPPLIER_COMPLIANCE_PIPELINE_FILTER_ITEMS.reduce(
		(accumulated, item) => ({
			...accumulated,
			[item.name]: nullSafeGetOrElse(item.name, statusCounts, 0),
		}),
		{}
	);

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description="Compliance requests from your clients will appear here."
		/>
	);

	return (
		<div className="flex flex-col gap-4 pb-6">
			<PipelineFilter
				items={SUPPLIER_COMPLIANCE_PIPELINE_FILTER_ITEMS}
				subItems={{}}
				value={pipelineValue}
				onChange={handlePipelineChange}
				counts={pipelineCounts}
			/>
			<Card bordered={false} bodyStyle={{ padding: 8 }}>
				<PaginatedReduxTable
					mode="list"
					// Sync filters (incl. search) to the URL so the shell's AdvancedFilters
					// search box clears correctly (it derives initialSearch from the URL).
					updateQueryParams={true}
					showHeader={false}
					collectionName={INSTANCES_COLLECTION}
					targetCollectionName={REQUESTS_LIST_TARGET_COLLECTION}
					columns={tableColumns}
					keyAccessor={(instance) => instance.id}
					initialPagination={INITIAL_PAGINATION}
					fetchData={fetchInstances}
					emptyState={emptyState}
				/>
			</Card>
			{linkingInstance && (
				<ChooseComplianceDocumentModal
					visible={!!linkingInstance}
					mode="link"
					instance={linkingInstance}
					onCancel={closeLinkModal}
					onSuccess={handleLinked}
				/>
			)}
			{viewingEvaluationInstance && (
				<ComplianceEvaluationModal
					visible={!!viewingEvaluationInstance}
					instance={viewingEvaluationInstance}
					onCancel={closeEvaluationModal}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => {
	const slice = state.compliance_request_instances;
	return {
		statusCounts: nullSafeGetOrElse(`counts.${STATUS_COUNTS_NAME}`, slice, {}),
		filters: nullSafeGetOrElse(`${REQUESTS_LIST_TARGET_COLLECTION}.filters`, slice, {}),
		pagination: nullSafeGetOrElse(
			`${REQUESTS_LIST_TARGET_COLLECTION}.pagination`,
			slice,
			INITIAL_PAGINATION
		),
	};
};

const mapDispatchToProps = (dispatch) => ({
	// Manual refetch after a link — replays the current filters + pagination so the
	// user stays on the same page. Pipeline (statuses) changes are refetched by
	// PaginatedReduxTable itself.
	refetchInstances: (filters, pagination) =>
		dispatch(
			complianceRequestInstancesRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
				{},
				REQUESTS_LIST_TARGET_COLLECTION,
				pagination,
				null,
				filters
			)
		),
	fetchStatusCounts: (countFilters) =>
		dispatch(
			complianceRequestInstancesRestCrudThunks(ROLE_TYPES.SUPPLIER).countBy(
				{ countBy: 'status', ...countFilters },
				STATUS_COUNTS_NAME
			)
		),
	fetchSummary: () => dispatch(fetchComplianceSummary(ROLE_TYPES.SUPPLIER)()),
	updateFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierComplianceRequestsPage);
